import {
  Alert,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  List,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { Dropzone, MS_EXCEL_MIME_TYPE } from "@mantine/dropzone";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconAlertTriangle,
  IconCloudUpload,
  IconDownload,
  IconFileSpreadsheet,
  IconReceipt2,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import readXlsxFile from "read-excel-file/browser";
import writeXlsxFile from "write-excel-file/browser";
import dayjs from "dayjs";
import { getProducts, updateProductBulkPrice } from "@/api/products";
import { getCategories } from "@/api/category";
import {
  bulkBranchOverrides,
  getBranchOverrides,
  getBranches,
} from "@/api/branches";
import {
  getChannels,
  getConnections,
  syncPrices,
  syncAllPrices,
} from "@/api/connections";
import { notify } from "@/lib/notify";
import { useAccess } from "@/hooks/useAccess";
import {
  downloadFile,
  identityFingerprint,
  META_MARKER,
  META_SHEET,
  protectSheetColumns,
} from "@/lib/xlsxProtect";
import { PageTitle } from "@/components/PageTitle";
import { EMPTY_LIST } from "@/lib/queryClient";

const SCHEMA_CORE = {
  "Ürün No": { prop: "id", type: Number, required: true },
  "Ürün Adı": { prop: "name", type: String, required: true },
  "Taban Fiyat": { prop: "basePrice", type: Number, required: true },
};

// Genel (Taban) sheet: ürün-seviyesi tek ent — import tüm şubelere yazar.
const SCHEMA_BASE = {
  ...SCHEMA_CORE,
  "Ent. Fiyatı": { prop: "integrationPrice", type: Number },
};

// Şube sheet: o şubenin şube + ent override'ları.
const SCHEMA_BRANCH = {
  ...SCHEMA_CORE,
  "Şube Fiyatı": { prop: "priceOverride", type: Number },
  "Ent. Fiyatı": { prop: "integrationPriceOverride", type: Number },
};

const HEADERS_CORE = ["Ürün No", "Kategori", "Ürün Adı", "Taban Fiyat"];
const HEADERS_BASE = [...HEADERS_CORE, "Ent. Fiyatı"];
const HEADERS_BRANCH = [...HEADERS_CORE, "Şube Fiyatı", "Ent. Fiyatı"];

// Ürün No, Kategori, Ürün Adı kimlik bilgisi — indirilen dosyada kilitli gelir.
const LOCKED_COLUMNS = [0, 1, 2];

// Taban fiyat tüm şubeleri etkiler (API'de requireFullAccess). Şube kapsamlı kullanıcıya kilitli
// gelir, yoksa düzenler ve yükleme 403 ile düşerdi.
const BASE_PRICE_COLUMN = 3;

// Parmak izine giren kimlik değerleri — hem indirmede hem yüklemede aynı sırayla üretilmeli.
const identityRows = (rows) =>
  rows.map((row) => LOCKED_COLUMNS.map((index) => row[index]?.value));

// Excel sekme adı kuralı: [ ] : * ? / \ yasak, en fazla 31 karakter.
const sheetName = (name) =>
  name
    .replace(/[[\]:*?/\\]/g, " ")
    .trim()
    .slice(0, 31) || "Fiyatlar";

// read-excel-file v9 artık `readXlsxFile(file, { schema })` ile eşleme yapmıyor; ham satır dizisi
// (`[{ sheet, data }]`, data[0] = başlık) döndürüyor. Başlığa göre elle eşleyip aynı doğrulamayı
// (zorunlu alan, sayı tipi) yapıyoruz.
function mapRowsWithSchema(data, schema) {
  const [header = [], ...body] = data;
  const columns = header.map((cell) => String(cell ?? "").trim());
  const objects = [];
  const errors = [];

  body.forEach((row, index) => {
    const object = {};
    let valid = true;
    for (const [columnName, def] of Object.entries(schema)) {
      const columnIndex = columns.indexOf(columnName);
      const raw = columnIndex === -1 ? null : row[columnIndex];
      if (raw == null || raw === "") {
        if (def.required) {
          errors.push({
            row: index + 2,
            column: columnName,
            error: "zorunlu alan boş",
          });
          valid = false;
        }
        continue;
      }
      if (def.type === Number) {
        const number = Number(raw);
        if (Number.isNaN(number)) {
          errors.push({
            row: index + 2,
            column: columnName,
            error: "sayı değil",
          });
          valid = false;
          continue;
        }
        object[def.prop] = number;
      } else {
        object[def.prop] = String(raw);
      }
    }
    if (valid) {
      objects.push(object);
    }
  });

  return { objects, errors };
}

// Yüklenen dosyanın panelden indirilmiş, aynı kapsam için üretilmiş ve kimlik kolonları
// değişmemiş olduğunu doğrular. Sorun varsa kullanıcıya gösterilecek mesajı, temizse null döner.
async function verifyProvenance(sheets, dataSheet, excelScope) {
  const [marker, scope, , , fingerprint] =
    sheets.find((item) => item.sheet === META_SHEET)?.data?.[0] ?? [];

  if (marker !== META_MARKER) {
    return 'Bu dosya panelden indirilen Excel değil. Önce "Excel İndir" ile dosyayı al, fiyatları onun üzerinde düzenle.';
  }
  if (String(scope) !== String(excelScope)) {
    return "Bu dosya başka bir kapsam için indirilmiş. Yukarıdan aynı şubeyi/genel seçeneğini seç ya da doğru dosyayı yükle.";
  }

  // Başlık satırı atlanır; kimlik kolonları indirmedeki sırayla parmak izine girer.
  const uploaded = (dataSheet?.data ?? [])
    .slice(1)
    .map((row) => LOCKED_COLUMNS.map((index) => row[index]));

  if ((await identityFingerprint(uploaded)) !== fingerprint) {
    return "Dosyadaki Ürün No / Kategori / Ürün Adı kolonları değişmiş ya da satır eklenmiş/silinmiş. İndirilen dosyada yalnızca fiyat kolonlarını düzenle.";
  }

  return null;
}

export default function Bulk() {
  // "Tüm Şubeler" kapsamı ürün-seviyesi fiyatı yazar ve her şubenin override'ını temizler —
  // API'de requireFullAccess korur. Şube müdürüne seçenek olarak hiç gösterilmiyor, yoksa
  // indirip yükleyip 403 yiyecekti. Şube seçenekleri zaten getBranches ile kapsanmış geliyor.
  const { allBranches } = useAccess();
  // Şube kapsamlı kullanıcıda taban fiyat kolonu da kilitli gelir.
  const lockedColumns = useMemo(
    () =>
      allBranches ? LOCKED_COLUMNS : [...LOCKED_COLUMNS, BASE_PRICE_COLUMN],
    [allBranches],
  );
  const queryClient = useQueryClient();
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [parseErrors, setParseErrors] = useState([]);
  const [errorsOpened, { open: openErrors, close: closeErrors }] =
    useDisclosure(false);
  // 'base' = tüm şubeler için taban fiyat; branchId = o şubenin şube + ent fiyatları.
  const [excelScope, setExcelScope] = useState(null);

  const { data: connections = EMPTY_LIST } = useQuery({
    queryKey: ["connections"],
    queryFn: getConnections,
  });
  const { data: channels = EMPTY_LIST } = useQuery({
    queryKey: ["channels"],
    queryFn: getChannels,
  });
  const { data: branches = EMPTY_LIST } = useQuery({
    queryKey: ["branches"],
    queryFn: getBranches,
  });
  const { data: products = EMPTY_LIST } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });

  const syncAllMutation = useMutation({
    // Tek istek: sunucu tüm aktif kanalları senkronize edip TEK özet mail atar (connection
    // başına ayrı mail yerine — Resend rate-limit'ine takılıp bazıları düşmesin).
    mutationFn: (branchId = null) => syncAllPrices(branchId),
    onSuccess: ({ pushed, count, failedConnections, rejectedProducts }) => {
      if (failedConnections || rejectedProducts) {
        const parts = [
          failedConnections
            ? `${failedConnections} entegrasyon başarısız`
            : null,
          rejectedProducts
            ? `${rejectedProducts} ürün entegrasyon tarafından reddedildi`
            : null,
        ].filter(Boolean);
        notify.error(
          `${pushed} fiyat gönderildi — ${parts.join(", ")}. Entegrasyonlar sayfasından kontrol et.`,
        );
        return;
      }
      if (!pushed) {
        notify.warning(
          `${count} entegrasyonda gönderilecek fiyat yok — hepsi güncel ya da filtrelenmiş.`,
        );
        return;
      }
      notify.success(`${count} entegrasyona ${pushed} fiyat gönderildi`);
    },
    onError: () => notify.error("Toplu senkronizasyon başarısız"),
  });

  const channelById = useMemo(
    () => new Map(channels.map((item) => [item.id, item.name])),
    [channels],
  );

  // Bağlantıları şubeye göre grupla; şube sırası `branches`ten (rank sıralı) gelsin, eşi olmayan
  // branchId'ler sona düşsün — böylece silinmiş bir şubenin bağlantısı bile listede kalır.
  const connectionGroups = useMemo(() => {
    const byBranch = new Map();
    for (const connection of connections) {
      const list = byBranch.get(connection.branchId);
      if (list) {
        list.push(connection);
      } else {
        byBranch.set(connection.branchId, [connection]);
      }
    }
    // Şube içinde bağlantılar KANAL ADINA göre sıralı: sıralama yoksa liste bağlantıların
    // eklenme sırasını gösteriyor ve aynı kanal her şubede başka yerde çıkıyordu — göz her
    // şubede aradığı düğmeyi baştan buluyordu.
    const byName = (left, right) =>
      (channelById.get(left.channelId) ?? "").localeCompare(
        channelById.get(right.channelId) ?? "",
        "tr",
      );

    const ordered = [];
    for (const branch of branches) {
      const items = byBranch.get(branch.id);
      if (items) {
        ordered.push({ branch, connections: [...items].sort(byName) });
        byBranch.delete(branch.id);
      }
    }
    for (const [branchId, items] of byBranch) {
      ordered.push({
        branch: { id: branchId, name: "?" },
        connections: [...items].sort(byName),
      });
    }
    return ordered;
  }, [connections, branches, channelById]);

  const handleDrop = async (files) => {
    const dropped = files[0];
    setFile(dropped);
    setRows(null);
    setParseErrors([]);

    const schema = excelScope === "base" ? SCHEMA_BASE : SCHEMA_BRANCH;

    // readXlsxFile bozuk / xlsx olmayan dosyada reject eder. Yakalanmazsa drop hiçbir iz
    // bırakmadan ölür ve partner dosyayı sürüklediğini bile göremez.
    const sheets = await readXlsxFile(dropped).catch((error) => {
      setParseErrors([
        `Dosya okunamadı — geçerli bir .xlsx değil (${error.message})`,
      ]);
      openErrors();
      return null;
    });

    if (!sheets) {
      return;
    }

    // Yalnızca panelden indirilen dosya kabul edilir: elle hazırlanmış / başka kapsam için
    // indirilmiş / kimlik kolonları değiştirilmiş dosya buradan geçemez.
    const dataSheet =
      sheets.find((item) => item.sheet !== META_SHEET) ?? sheets[0];
    const provenanceError = await verifyProvenance(
      sheets,
      dataSheet,
      excelScope,
    );

    if (provenanceError) {
      setParseErrors([provenanceError]);
      openErrors();
      return;
    }

    // v9: readXlsxFile → [{ sheet, data }], data = ham satır dizisi (data[0] = başlık). Veri
    // sayfasını başlığa göre elle eşle.
    const { objects: parsed, errors } = mapRowsWithSchema(
      dataSheet?.data ?? [],
      schema,
    );

    if (errors.length) {
      setParseErrors(
        errors.map(
          (item) => `${item.row}. satır — ${item.column}: ${item.error}`,
        ),
      );
      openErrors();
      return;
    }
    if (!parsed.length) {
      setParseErrors(["Boş Excel dosyası"]);
      openErrors();
      return;
    }
    setRows(parsed);
  };

  const downloadMutation = useMutation({
    mutationFn: async () => {
      const branchScope =
        excelScope === "base" ? null : Number(excelScope) || null;
      const headers = branchScope ? HEADERS_BRANCH : HEADERS_BASE;
      const [freshProducts, freshCategories, overrides] = await Promise.all([
        getProducts(),
        getCategories(),
        branchScope ? getBranchOverrides(branchScope) : Promise.resolve([]),
      ]);
      const categoryById = new Map(
        freshCategories.map((item) => [item.id, item.name]),
      );
      const overrideByProductId = new Map(
        overrides.map((item) => [item.productId, item]),
      );
      const headerRow = headers.map((value) => ({
        value,
        fontWeight: "bold",
        backgroundColor: "#f2c30a",
        align: "center",
        fontSize: 15,
      }));
      // Sıra API'den geliyor: listProducts kategori sırası + ürün sırasına göre döndürüyor.
      const dataRows = freshProducts.map((product) => {
        const core = [
          { type: Number, value: product.id },
          { type: String, value: categoryById.get(product.categoryId) ?? "" },
          { type: String, value: product.name },
          { type: Number, value: product.basePrice },
        ];
        if (!branchScope) {
          // Genel ent = ürün-seviyesi integrationPrice (boşsa taban).
          return [
            ...core,
            {
              type: Number,
              value: product.integrationPrice ?? product.basePrice,
            },
          ];
        }
        const override = overrideByProductId.get(product.id);
        return [
          ...core,
          { type: Number, value: override?.priceOverride ?? product.basePrice },
          {
            type: Number,
            value:
              override?.integrationPriceOverride ??
              product.integrationPrice ??
              product.basePrice,
          },
        ];
      });
      const branchSlug = branchScope
        ? `${branches.find((item) => item.id === branchScope)?.slug ?? branchScope}-`
        : "";
      // Gizli imza sayfası: yükleme sırasında dosyanın panelden indirildiğini, aynı kapsam
      // için üretildiğini ve kimlik kolonlarının değişmediğini buradan doğruluyoruz.
      const fingerprint = await identityFingerprint(identityRows(dataRows));
      const blob = await writeXlsxFile([
        {
          data: [headerRow, ...dataRows],
          // Varsayılan "Sheet1" yerine kapsamı söyleyen ad — şube dosyasında hangi şubenin
          // fiyatlarına baktığı sekmeden görünsün.
          sheet: branchScope
            ? sheetName(
                branches.find((item) => item.id === branchScope)?.name ??
                  "Şube",
              )
            : "Genel Fiyatlar",
          columns: headers.map((_, index) => ({
            width: index === 2 ? 40 : 22,
          })),
        },
        {
          data: [
            [
              { value: META_MARKER },
              { value: excelScope },
              { value: dayjs().toISOString() },
              { type: Number, value: dataRows.length },
              { value: fingerprint },
            ],
          ],
          sheet: META_SHEET,
        },
      ]).toBlob();
      // Ürün No / Kategori / Ürün Adı kilitli — import bu kolonlara göre eşleştiği için
      // kazara düzenlenmemeli. Kalan fiyat kolonları sayı-kısıtlı ve açık.
      const protectedFile = protectSheetColumns(await blob.arrayBuffer(), {
        columnCount: headers.length,
        lockedColumns,
        rows: dataRows,
        hiddenSheet: META_SHEET,
      });
      downloadFile(
        new Blob([protectedFile], { type: blob.type }),
        `urunler-${branchSlug}${dayjs().format("YYYY-MM-DD_HH-mm")}.xlsx`,
      );
    },
    onSuccess: () => notify.success("Excel indirildi"),
    onError: () => notify.error("Excel oluşturulamadı"),
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const branchScope =
        excelScope === "base" ? null : Number(excelScope) || null;
      const freshProducts = await getProducts();
      const productById = new Map(
        freshProducts.map((product) => [product.id, product]),
      );

      const baseItems = rows
        .filter(
          (row) =>
            productById.has(row.id) &&
            productById.get(row.id).basePrice !== row.basePrice,
        )
        .map((row) => ({ id: row.id, basePrice: row.basePrice }));
      // Taban fiyat ürün-seviyesi ve tüm şubeleri etkiler; şube kapsamlı kullanıcıda hiç
      // gönderilmez (kolonu zaten kilitli geliyor).
      if (allBranches && baseItems.length)
        await updateProductBulkPrice(baseItems);

      if (!branchScope) {
        // Genel ent kolonu = ürün-seviyesi Product.integrationPrice; değişeni yaz.
        const entItems = rows
          .filter((row) => {
            const product = productById.get(row.id);
            if (!product || row.integrationPrice == null) return false;
            return (
              row.integrationPrice !==
              (product.integrationPrice ?? product.basePrice)
            );
          })
          .map((row) => ({
            id: row.id,
            integrationPrice: row.integrationPrice,
          }));
        if (entItems.length) await updateProductBulkPrice(entItems);
        return { changed: baseItems.length + entItems.length };
      }

      // Şube kapsamında: override'ı taban ile aynıysa temizle (null), farklıysa yaz.
      const overrides = await getBranchOverrides(branchScope);
      const overrideByProductId = new Map(
        overrides.map((item) => [item.productId, item]),
      );
      const overrideItems = rows
        .filter((row) => productById.has(row.id))
        .map((row) => {
          const base = row.basePrice;
          const product = productById.get(row.id);
          const current = overrideByProductId.get(row.id);
          const currentPrice = current?.priceOverride ?? base;
          // İndirme bu kolona `integrationPriceOverride ?? product.integrationPrice ??
          // basePrice` yazar; aynı zinciri kullanmazsak ürün-seviyesi integrationPrice'ı
          // olan her satır, dosya hiç düzenlenmeden yüklendiğinde hayalet override alır.
          const currentIntegration =
            current?.integrationPriceOverride ??
            product.integrationPrice ??
            base;
          const item = { productId: row.id };
          if (row.priceOverride != null && row.priceOverride !== currentPrice) {
            item.priceOverride = row.priceOverride;
          }
          if (
            row.integrationPriceOverride != null &&
            row.integrationPriceOverride !== currentIntegration
          ) {
            item.integrationPriceOverride = row.integrationPriceOverride;
          }
          return item;
        })
        .filter(
          (item) =>
            item.priceOverride !== undefined ||
            item.integrationPriceOverride !== undefined,
        );
      if (overrideItems.length)
        await bulkBranchOverrides(branchScope, overrideItems);

      return { changed: baseItems.length + overrideItems.length };
    },
    onSuccess: ({ changed }) => {
      if (!changed) {
        notify.info("Fiyatlar aynı, güncellenecek bir şey yok");
        return;
      }
      notify.success(`${changed} fiyat güncellendi`);
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["branch-overrides"] });
      setFile(null);
      setRows(null);
    },
    onError: () => notify.error("Toplu güncelleme başarısız"),
  });

  const syncMutation = useMutation({
    mutationFn: (connectionId) => syncPrices(connectionId),
    onSuccess: ({
      pushed,
      skipped = 0,
      filtered = 0,
      noPrice = 0,
      missingRemoteIds = [],
      failed = [],
    }) => {
      if (failed.length) {
        notify.error(
          `${pushed} fiyat gönderildi, ${failed.length} ürün reddedildi`,
        );
        return;
      }
      const reasons = [
        noPrice ? `${noPrice} üründe ent. fiyat yok` : null,
        filtered ? `${filtered} ürün grup filtresiyle hariç` : null,
        skipped ? `${skipped} fiyat zaten güncel` : null,
        missingRemoteIds.length
          ? `${missingRemoteIds.length} ürün entegrasyonda bulunamadı`
          : null,
      ].filter(Boolean);
      const detail = reasons.length ? ` (${reasons.join(", ")})` : "";
      if (pushed) {
        notify.success(`${pushed} fiyat gönderildi${detail}`);
      } else {
        notify.info(`Gönderilecek fiyat yok${detail}`);
      }
    },
    onError: () => notify.error("Senkronizasyon başarısız"),
  });

  const busy =
    downloadMutation.isPending ||
    uploadMutation.isPending ||
    syncMutation.isPending ||
    syncAllMutation.isPending;

  const confirmSync = (connection, branchName) => {
    const channelName = channelById.get(connection.channelId) ?? "Entegrasyon";

    modals.openConfirmModal({
      title: "Fiyatları entegrasyona gönder",
      centered: true,
      children: (
        <Text size="sm">
          <b>
            {branchName} — {channelName}
          </b>{" "}
          entegrasyonuna eşleştirilmiş ürünlerin fiyatları gönderilecek.
          Gönderilen fiyat platformda anında geçerli olur ve geri alınamaz.
        </Text>
      ),
      labels: { confirm: "Gönder", cancel: "Vazgeç" },
      onConfirm: () => syncMutation.mutate(connection.id),
    });
  };

  const confirmSyncAll = (branchId) => {
    const active = connections.filter(
      (connection) =>
        connection.status === "active" &&
        (branchId == null || connection.branchId === branchId),
    ).length;
    const branchName = branches.find((branch) => branch.id === branchId)?.name;
    const scope =
      branchId == null
        ? "Tüm şubelerde"
        : branchName
          ? `${branchName} şubesinde`
          : "Seçili şubede";

    modals.openConfirmModal({
      title: "Fiyatları entegrasyonlara gönder",
      centered: true,
      children: (
        <Text size="sm">
          {scope} <b>{active} aktif entegrasyona</b> eşleştirilmiş ürünlerin
          fiyatları gönderilecek. Gönderilen fiyat platformda anında geçerli
          olur ve geri alınamaz.
        </Text>
      ),
      labels: { confirm: "Gönder", cancel: "Vazgeç" },
      onConfirm: () => syncAllMutation.mutate(branchId),
    });
  };

  const scopeOptions = useMemo(() => {
    const branchOptions = branches.map((branch) => ({
      value: String(branch.id),
      label: branch.name,
    }));

    return allBranches
      ? [{ value: "base", label: "Tüm Şubeler" }, ...branchOptions]
      : branchOptions;
  }, [branches, allBranches]);

  // Kapsam başlangıçta boş: tam yetkili 'Tüm Şubeler' ile açılır, şube müdürü ilk şubesiyle.
  // Şubeler asenkron geldiği için ilk render'da henüz bilinmiyor.
  useEffect(() => {
    if (excelScope === null && scopeOptions.length) {
      setExcelScope(scopeOptions[0].value);
    }
  }, [excelScope, scopeOptions]);

  return (
    <Stack gap="lg">
      <PageTitle>Toplu İşlemler</PageTitle>
      <Group>
        <ThemeIcon size={46} radius="md" variant="light" color="chocolate">
          <IconReceipt2 size={26} stroke={1.6} />
        </ThemeIcon>
        <div>
          <Title order={2}>Toplu İşlemler</Title>
          <Text c="dimmed" size="sm">
            Excel ile toplu fiyat güncelle, entegrasyon fiyatlarını senkronla
          </Text>
        </div>
      </Group>

      {/* Şube kapsamlı kullanıcıya hiç şube atanmamışsa sayfada yapabileceği bir şey yok —
                boş bir Kapsam seçimiyle baş başa bırakmak yerine sebebini söyle. */}
      {!scopeOptions.length && (
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
        >
          Sana henüz şube atanmamış. Toplu fiyat işlemleri için marka
          yöneticinden şube ataması iste.
        </Alert>
      )}

      <Grid gutter="lg" align="stretch">
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Card withBorder radius="md" p="lg" h="100%">
            <Group justify="space-between" mb="md" align="flex-start">
              <Group gap="sm">
                <ThemeIcon
                  variant="light"
                  color="chocolate"
                  radius="md"
                  size="md"
                >
                  <IconFileSpreadsheet size={18} />
                </ThemeIcon>
                <div>
                  <Text fw={600} size="sm">
                    Toplu Fiyat Güncelleme
                  </Text>
                  <Text size="xs" c="dimmed">
                    Şablonu indir, fiyatları düzenle, geri yükle — sadece
                    değişenler güncellenir
                  </Text>
                </div>
              </Group>
              <Group gap="xs" align="flex-end">
                <Select
                  w={190}
                  label="Kapsam"
                  withCheckIcon={false}
                  allowDeselect={false}
                  data={scopeOptions}
                  value={excelScope}
                  onChange={(value) => {
                    setExcelScope(value);
                    setFile(null);
                    setRows(null);
                    setParseErrors([]);
                  }}
                />
                <Button
                  variant="default"
                  leftSection={<IconDownload size={16} />}
                  loading={downloadMutation.isPending}
                  disabled={busy || !excelScope || products.length === 0}
                  onClick={() => downloadMutation.mutate()}
                >
                  Excel İndir
                </Button>
              </Group>
            </Group>

            <Dropzone
              onDrop={handleDrop}
              accept={MS_EXCEL_MIME_TYPE}
              maxFiles={1}
              multiple={false}
              loading={uploadMutation.isPending}
              disabled={busy}
            >
              <Stack
                align="center"
                gap="xs"
                mih={160}
                justify="center"
                style={{ pointerEvents: "none" }}
              >
                <Dropzone.Accept>
                  <IconCloudUpload size={48} stroke={1.4} />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={48} stroke={1.4} />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconFileSpreadsheet size={48} stroke={1.4} />
                </Dropzone.Idle>
                <Text fw={600}>Excel dosyasını sürükle ya da tıkla</Text>
                <Group gap="xs">
                  <Text size="xs" c="dimmed">
                    yalnızca .xlsx
                  </Text>
                  {file && (
                    <Badge
                      variant="light"
                      color={
                        rows ? "teal" : parseErrors.length ? "red" : "gray"
                      }
                    >
                      {file.name}
                    </Badge>
                  )}
                </Group>
              </Stack>
            </Dropzone>

            {parseErrors.length > 0 && (
              <Group justify="space-between" mt="sm">
                <Text size="sm" c="red">
                  {parseErrors.length} satırda hata var
                </Text>
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  onClick={openErrors}
                >
                  Hataları gör
                </Button>
              </Group>
            )}

            <Button
              mt="md"
              fullWidth
              leftSection={<IconCloudUpload size={16} />}
              disabled={!rows || !excelScope || busy}
              loading={uploadMutation.isPending}
              onClick={() => uploadMutation.mutate()}
            >
              Yükle ve Güncelle
            </Button>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card withBorder radius="md" p="lg" h="100%">
            <Group gap="sm" mb="md">
              <ThemeIcon
                variant="light"
                color="chocolate"
                radius="md"
                size="md"
              >
                <IconRefresh size={18} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="sm">
                  Entegrasyon Fiyat Senkronizasyonu
                </Text>
                <Text size="xs" c="dimmed">
                  Eşleştirilmiş ürünlerin entegrasyon fiyatlarını ilgili
                  platforma toplu it
                </Text>
              </div>
            </Group>
            {connections.length === 0 ? (
              <Text size="sm" c="dimmed">
                Henüz bağlantı yok — Entegrasyonlar sayfasından ekleyin.
              </Text>
            ) : (
              <Stack gap="sm">
                <Button
                  leftSection={<IconRefresh size={16} />}
                  loading={
                    syncAllMutation.isPending &&
                    syncAllMutation.variables == null
                  }
                  disabled={busy}
                  onClick={() => confirmSyncAll(null)}
                >
                  Tüm aktif entegrasyonları gönder
                </Button>
                <ScrollArea.Autosize mah={360} type="hover" offsetScrollbars>
                  <Stack gap="md">
                    {connectionGroups.map(
                      ({ branch, connections: branchConnections }) => (
                        <div key={branch.id}>
                          <Group
                            justify="space-between"
                            gap="xs"
                            wrap="nowrap"
                            mb={6}
                          >
                            <Text
                              size="xs"
                              fw={700}
                              c="dimmed"
                              tt="uppercase"
                              truncate
                            >
                              {branch.name}
                            </Text>
                            <Button
                              size="compact-sm"
                              variant="subtle"
                              leftSection={<IconRefresh size={13} />}
                              loading={
                                syncAllMutation.isPending &&
                                syncAllMutation.variables === branch.id
                              }
                              disabled={busy}
                              onClick={() => confirmSyncAll(branch.id)}
                            >
                              Hepsini gönder
                            </Button>
                          </Group>
                          <Group gap="xs">
                            {branchConnections.map((connection) => (
                              <Button
                                key={connection.id}
                                size="compact-sm"
                                variant="default"
                                loading={
                                  syncMutation.isPending &&
                                  syncMutation.variables === connection.id
                                }
                                disabled={
                                  busy &&
                                  syncMutation.variables !== connection.id
                                }
                                onClick={() =>
                                  confirmSync(connection, branch.name)
                                }
                              >
                                {channelById.get(connection.channelId) ?? "?"}
                              </Button>
                            ))}
                          </Group>
                        </div>
                      ),
                    )}
                  </Stack>
                </ScrollArea.Autosize>
              </Stack>
            )}
          </Card>
        </Grid.Col>
      </Grid>

      <Modal
        opened={errorsOpened}
        onClose={closeErrors}
        title="Excel Hataları"
        centered
        radius="md"
        size="lg"
      >
        <List spacing="xs" size="sm" withPadding>
          {parseErrors.map((message) => (
            <List.Item key={message}>{message}</List.Item>
          ))}
        </List>
      </Modal>
    </Stack>
  );
}
